/**
 * Tiny self-contained colour math (no deps — shared must stay dependency-free).
 * Just enough oklab/oklch + sRGB to replicate HeroUI's `color-mix(in oklab, …)`
 * derivation and to check WCAG contrast. Used by deriveTheme.
 */

export interface Rgb {
  r: number; // 0..255
  g: number;
  b: number;
}

export interface Oklch {
  L: number; // 0..1
  C: number; // 0..~0.4
  h: number; // radians
}

const clamp01 = (n: number): number => (n < 0 ? 0 : n > 1 ? 1 : n);

export function parseHex(hex: string): Rgb {
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) h = h[0]! + h[0]! + h[1]! + h[1]! + h[2]! + h[2]!;
  const int = parseInt(h, 16);
  return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
}

export function toHex({ r, g, b }: Rgb): string {
  const c = (n: number) => Math.round(clamp01(n / 255) * 255).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

/** sRGB channel (0..1) → linear. */
function toLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}
function fromLinear(c: number): number {
  return c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
}

interface Oklab {
  L: number;
  a: number;
  b: number;
}

export function rgbToOklab({ r, g, b }: Rgb): Oklab {
  const lr = toLinear(r / 255);
  const lg = toLinear(g / 255);
  const lb = toLinear(b / 255);
  const l = 0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb;
  const m = 0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb;
  const s = 0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb;
  const l_ = Math.cbrt(l);
  const m_ = Math.cbrt(m);
  const s_ = Math.cbrt(s);
  return {
    L: 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_,
    a: 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_,
    b: 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_,
  };
}

export function oklabToRgb({ L, a, b }: Oklab): Rgb {
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.291485548 * b;
  const l = l_ * l_ * l_;
  const m = m_ * m_ * m_;
  const s = s_ * s_ * s_;
  const lr = 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
  const lg = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
  const lb = -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s;
  return {
    r: clamp01(fromLinear(lr)) * 255,
    g: clamp01(fromLinear(lg)) * 255,
    b: clamp01(fromLinear(lb)) * 255,
  };
}

export function rgbToOklch(rgb: Rgb): Oklch {
  const { L, a, b } = rgbToOklab(rgb);
  return { L, C: Math.sqrt(a * a + b * b), h: Math.atan2(b, a) };
}

export function oklchToHex({ L, C, h }: Oklch): string {
  return toHex(oklabToRgb({ L, a: Math.cos(h) * C, b: Math.sin(h) * C }));
}

/** Mix two hex colours in oklab by `t` (0 = a, 1 = b) — matches CSS
 *  `color-mix(in oklab, a (1-t)%, b t%)`. */
export function mixHex(a: string, b: string, t: number): string {
  const A = rgbToOklab(parseHex(a));
  const B = rgbToOklab(parseHex(b));
  return toHex(
    oklabToRgb({
      L: A.L + (B.L - A.L) * t,
      a: A.a + (B.a - A.a) * t,
      b: A.b + (B.b - A.b) * t,
    }),
  );
}

/** `#rrggbb` → `"r g b"` (space-separated channels for the web's
 *  `rgb(var(--token) / alpha)` token form). */
export function hexToTriplet(hex: string): string {
  const { r, g, b } = parseHex(hex);
  return `${Math.round(r)} ${Math.round(g)} ${Math.round(b)}`;
}

/** `rgba(r,g,b,alpha)` from a hex + alpha (used by mobile overlay tints). */
export function hexToRgba(hex: string, alpha: number): string {
  const { r, g, b } = parseHex(hex);
  return `rgba(${Math.round(r)},${Math.round(g)},${Math.round(b)},${alpha})`;
}

/** WCAG relative luminance (0..1). */
export function luminance(rgb: Rgb): number {
  const lr = toLinear(rgb.r / 255);
  const lg = toLinear(rgb.g / 255);
  const lb = toLinear(rgb.b / 255);
  return 0.2126 * lr + 0.7152 * lg + 0.0722 * lb;
}

/** WCAG contrast ratio between two hex colours (1..21). */
export function contrastRatio(a: string, b: string): number {
  const la = luminance(parseHex(a));
  const lb = luminance(parseHex(b));
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}
